import { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { SignatureCanvas } from './SignatureCanvas';
import { SigningOtpCard } from './SigningOtpCard';
import { signStep, type SigningPlan } from './signingFlowApi';

const card = 'bg-white rounded-2xl border border-black/[0.08] shadow-sm';
const input =
  'block w-full px-4 py-3 text-base text-[#37352F] bg-black/[0.03] border border-black/[0.09] rounded-xl focus:outline-none focus:ring-2 focus:ring-black/10 placeholder-[#9D9892]';
const primary =
  'w-full inline-flex justify-center items-center gap-2 px-5 py-3.5 text-base font-semibold text-white rounded-xl bg-[#37352F] disabled:opacity-40';

const timeIt = (iso: string) =>
  new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });

interface Props {
  token: string;
  plan: SigningPlan;
  signerName: string;
  onSignerNameChange: (v: string) => void;
  notifyEmail: string;
  onNotifyEmailChange: (v: string) => void;
  notifyPhone: string;
  onNotifyPhoneChange: (v: string) => void;
  completing: boolean;
  onComplete: () => void;
}

/**
 * Firma a più passi: (codice OTP) → una firma per ogni punto del contratto,
 * ognuna salvata subito dal server con la sua ora → conclusione.
 * Le clausole vessatorie hanno il loro passo con l'elenco degli articoli.
 */
export function MultiStepSigning(p: Props) {
  const { plan, token } = p;
  const [otpOk, setOtpOk] = useState(plan.otp.mode === 'off' || plan.otp.verified);
  const [signed, setSigned] = useState<Record<string, string>>(
    Object.fromEntries(plan.signed.map((s) => [s.key, s.signedAt])),
  );
  const [image, setImage] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = plan.steps.find((s) => !signed[s.key]) ?? null;
  const doneCount = plan.steps.filter((s) => signed[s.key]).length;
  const nameOk = p.signerName.trim().length >= 2;

  const signCurrent = async () => {
    if (!current || !image) return;
    setBusy(true);
    setError(null);
    try {
      const r = await signStep(token, current.key, image, p.signerName.trim());
      setSigned((m) => ({ ...m, [current.key]: r.signedAt }));
      setImage(null);
      setAccepted(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!otpOk) {
    return (
      <SigningOtpCard
        token={token}
        otp={plan.otp}
        onVerified={() => setOtpOk(true)}
        onSkip={() => setOtpOk(true)}
      />
    );
  }

  return (
    <section className={card}>
      <div className="px-6 sm:px-8 py-5 border-b border-black/[0.06]">
        <h2 className="text-base font-semibold text-[#37352F]">Firma il documento</h2>
        <p className="text-xs text-[#9D9892] mt-0.5">
          {plan.steps.length} firme, una per ogni parte del contratto · {doneCount} di {plan.steps.length} fatte
        </p>
        <div className="mt-3 h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
          <div className="h-full bg-[#37352F] transition-all" style={{ width: `${(doneCount / plan.steps.length) * 100}%` }} />
        </div>
      </div>

      <div className="px-6 sm:px-8 py-5 space-y-5">
        <div>
          <label className="block text-xs font-medium text-[#6B6762] uppercase tracking-wide mb-1.5">Il tuo nome</label>
          <input
            id="signer-name-input"
            value={p.signerName}
            onChange={(e) => p.onSignerNameChange(e.target.value)}
            disabled={doneCount > 0}
            placeholder="Nome e cognome"
            className={input}
          />
        </div>

        <ol className="space-y-3">
          {plan.steps.map((s, i) => {
            const at = signed[s.key];
            const isCurrent = current?.key === s.key;
            return (
              <li key={s.key} className={`rounded-xl border ${isCurrent ? 'border-black/[0.15]' : 'border-black/[0.06]'} px-4 py-3`}>
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${at ? 'bg-[#1F8A5B] text-white' : 'bg-[#F7F6F3] text-[#37352F]'}`}>
                    {at ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-semibold ${at || isCurrent ? 'text-[#37352F]' : 'text-[#9D9892]'}`}>{s.title}</div>
                    {at && <div className="text-xs text-[#1F8A5B]">Firmato alle {timeIt(at)}</div>}
                  </div>
                </div>

                {isCurrent && (
                  <div className="mt-3 space-y-3">
                    {s.text && (
                      <div className="max-h-56 overflow-y-auto rounded-lg bg-[#F7F6F3] px-3 py-2.5 text-sm leading-relaxed text-[#37352F]">
                        {s.text}
                      </div>
                    )}
                    <label className="flex items-start gap-3 text-sm text-[#37352F]">
                      <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 h-4 w-4 accent-[#37352F]" />
                      <span>{s.text ? 'Ho letto e approvo specificamente quanto sopra.' : 'Ho letto e approvo.'}</span>
                    </label>
                    <SignatureCanvas key={s.key} onChange={setImage} />
                    <button type="button" onClick={signCurrent} disabled={!nameOk || !accepted || !image || busy} className={primary}>
                      {busy && <Loader2 className="h-5 w-5 animate-spin" />}
                      Firma questo punto
                    </button>
                    {!nameOk && <p className="text-xs text-[#9D9892] text-center">Scrivi prima il tuo nome</p>}
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        {error && <p className="text-sm text-[#C4362C]">{error}</p>}

        {!current && (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-[#37352F]">Dove vuoi ricevere il contratto firmato?</div>
            <input type="email" value={p.notifyEmail} onChange={(e) => p.onNotifyEmailChange(e.target.value)} placeholder="Email" className={input} />
            <input type="tel" value={p.notifyPhone} onChange={(e) => p.onNotifyPhoneChange(e.target.value)} placeholder="WhatsApp, es. +39 333 1234567" className={input} />
            <button type="button" onClick={p.onComplete} disabled={p.completing} className={primary}>
              {p.completing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
              {p.completing ? 'Preparo il contratto firmato…' : 'Concludi la firma'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
